import { ModalWindow } from '@cian/ui-kit/modal';

import * as React from 'react';

import { useToggleState } from '../../../hooks/useToggleState';

import { CoworkingPlacementApplicationForm } from '../CoworkingPlacementApplicationForm';
import { ModalContentStageFilling } from '../ModalContentStageFilling';
import { ModalContentStageSuccess } from '../ModalContentStageSuccess';

import * as styles from './CoworkingPlacementApplicationModal.css';
import { normalizePlacementApplicationFormFields } from './internal/utils';
import { useFetchPostApplyForPublication } from './internal/hooks';
import { EStageTypes, formStages, MODAL_MAX_WIDTH, MODAL_WIDTH } from './internal/constants';
import {
  HandleModalClose,
  HandleStageFillingNext,
  ICoworkingPlacementApplicationModalProps,
} from './types';

export const CoworkingPlacementApplicationModal: React.FC<ICoworkingPlacementApplicationModalProps> = props => {
  const { children, logger, httpApi } = props;

  const {
    callRequest: callApplyForPublicationRequest,
    pending: applyForPublicationPending,
  } = useFetchPostApplyForPublication(httpApi, logger);

  const [formStageState, setFormStageState] = React.useState<EStageTypes>(EStageTypes.filling);

  const [modalVisible, toggleModalVisibility, setModalVisibility] = useToggleState();

  const handleStageFillingNext = React.useCallback<HandleStageFillingNext>(
    formState => {
      const requestParameters = normalizePlacementApplicationFormFields(formState);

      callApplyForPublicationRequest(
        requestParameters,
        {
          onSuccess: () => {
            setFormStageState(formStages[formStageState].next);
          },
        },
      );
    },
    [callApplyForPublicationRequest, formStageState],
  );

  const handleModalClose = React.useCallback<HandleModalClose>(() => {
    setFormStageState(EStageTypes.filling);
    setModalVisibility(false);
  }, [setModalVisibility]);

  const modalContent = (() => {
    switch (formStageState) {
      case EStageTypes.filling:
        return (
          <ModalContentStageFilling
            description="Чтобы получить доступ к размещению коворкинга, оставьте свои контакты. Наш специалист свяжется с вами в течение
        2 рабочих дней, уточнит детали и всё подробно расскажет."
            title="Заявка на размещение коворкинга"
          >
            <CoworkingPlacementApplicationForm
              isDisableFieldsOnPending
              onSubmit={handleStageFillingNext}
              pending={applyForPublicationPending}
            />
          </ModalContentStageFilling>
        );

      case EStageTypes.success:
        return <ModalContentStageSuccess onNextStage={handleModalClose} />;

      // istanbul ignore next
      default:
        return null;
    }
  })();

  return (
    <>
      <ModalWindow
        escape
        fixed={false}
        maxWidth={MODAL_MAX_WIDTH}
        onClose={handleModalClose}
        open={modalVisible}
        outside={formStageState !== EStageTypes.filling}
        size="M"
        width={MODAL_WIDTH}
      >
        <div className={styles['content-container']}>{modalContent}</div>
      </ModalWindow>
      {children(modalVisible, toggleModalVisibility, setModalVisibility)}
    </>
  );
};

CoworkingPlacementApplicationModal.displayName = 'CoworkingPlacementApplicationModal';
